import { InvalidConfigError } from "@dxos/protocols";
import { create, fromJsonString } from "@bufbuild/protobuf";
import defaultsDeep from "lodash.defaultsdeep";
import isMatch from "lodash.ismatch";
import { ConfigSchema } from "@dxos/protocols/buf/dxos/config_pb";
import { getDeep, setDeep } from "@dxos/util";
//#region src/config.ts
/**
* Maps the given objects onto a flattened set of (key x values).
* Expects parsed yaml content of the form:
*
* ```
* ENV_VAR:
*   path: config.selector.path
* ```
*
* @param {object} spec
* @param {object} values
* @return {object}
*/
var mapFromKeyValues = (spec, values) => {
	const config = {};
	for (const [key, { path, type }] of Object.entries(spec)) {
		let value = values[key];
		if (value !== void 0) {
			if (type) switch (type) {
				case "boolean":
					value = String(value).toLowerCase() === "true" || value === "1";
					break;
				case "number":
					value = Number(value);
					break;
				case "string": break;
				case "json":
					value = value ? JSON.parse(value) : null;
					break;
				default: throw new Error(`Invalid type: ${type}`);
			}
			setDeep(config, path.split("."), value);
		}
	}
	return config;
};
/**
* Maps the given flattend set of (key x values) onto a JSON object.
* @param {object} spec
* @param {object} values
*/
var mapToKeyValues = (spec, values) => {
	const config = {};
	for (const [key, { path, type }] of Object.entries(spec)) {
		const value = getDeep(values, path.split("."));
		if (value !== void 0) switch (type) {
			case "json":
				config[key] = JSON.stringify(value);
				break;
			default: config[key] = value;
		}
	}
	return config;
};
var isPlainNode = (value) => value !== null && typeof value === "object" && Object.getPrototypeOf(value) === Object.prototype;
/**
* Removes the protobuf-es `$typeName` marker from a config tree.
*
* `defaultsDeep` copies `$typeName` onto plain objects that came from another source, and
* protobuf-es then treats those objects as already-constructed messages and skips normalising
* their plain descendants -- producing a `Config.values` that `toBinary` cannot encode.
*/
var stripTypeNames = (value) => {
	if (Array.isArray(value)) return value.map((item) => stripTypeNames(item));
	if (!isPlainNode(value)) return value;
	const result = {};
	for (const [key, item] of Object.entries(value)) {
		if (key === "$typeName") continue;
		result[key] = key === "$unknown" ? item : stripTypeNames(item);
	}
	return result;
};
/**
* Copies `$unknown` records from a merged config tree onto the message `create` built from it.
*
* `MessageInitShape` excludes `$unknown`, so normalising a tree whose `$typeName` markers were
* stripped drops the wire fields a source decoded but this build's schema does not know.
*/
var restoreUnknownFields = (target, source) => {
	if (!isPlainNode(target) || !isPlainNode(source)) return;
	if (source.$unknown !== void 0) target.$unknown = source.$unknown;
	for (const [key, value] of Object.entries(source)) if (key !== "$unknown") restoreUnknownFields(target[key], value);
};
/**
* Validates a config object and normalises it into a buf message.
* Field types are checked by the compiler through `ConfigInit`, which is why this no longer runs the
* protobuf.js `verify` pass; loaders that read untrusted YAML validate as they parse.
*/
var validateConfig = (config) => {
	if (!("version" in config)) throw new InvalidConfigError({ message: "Version not specified" });
	if (config?.version !== 1) throw new InvalidConfigError({ message: `Invalid config version: ${config.version}` });
	try {
		return create(ConfigSchema, config);
	} catch (err) {
		throw new InvalidConfigError({ message: String(err) });
	}
};
/** Keeps non-string `Struct` values out of string-only consumers of `runtime.app.env`. */
var getEnvString = (config, key) => {
	const value = config?.values.runtime?.app?.env?.[key];
	return typeof value === "string" ? value : void 0;
};
/** Validates config data from an untrusted source -- a file, an endpoint, browser storage. */
var parseConfig = (data, source) => {
	try {
		return fromJsonString(ConfigSchema, JSON.stringify(data), { ignoreUnknownFields: true });
	} catch (err) {
		throw new InvalidConfigError({ message: `Invalid config from ${source}: ${err}` });
	}
};
/**
* Global configuration object.
* NOTE: Config objects are immutable.
*/
var Config = class {
	_config;
	/**
	* Creates an immutable instance.
	* @constructor
	*/
	constructor(config = {}, ...objects) {
		const merged = defaultsDeep(stripTypeNames(config), ...objects.map(stripTypeNames), { version: 1 });
		this._config = validateConfig(merged);
		restoreUnknownFields(this._config, merged);
	}
	/**
	* Returns an immutable config JSON object.
	*/
	get values() {
		return this._config;
	}
	/**
	* Returns the given config property.
	*
	* @param key A key in the config object. Can be a nested property with keys separated by dots: 'services.signal.server'.
	* @param defaultValue Default value to return if option is not present in the config.
	* @returns The config value or undefined if the option is not present.
	*/
	get(key, defaultValue) {
		return getDeep(this._config, key.split(".")) ?? defaultValue;
	}
	/**
	* Get unique key.
	*/
	find(path, test) {
		const values = getDeep(this._config, path.split("."));
		if (!Array.isArray(values)) return;
		return values.find((value) => isMatch(value, test));
	}
	/**
	* Returns the given config property or throw if it doesn't exist.
	*
	* @param key A key in the config object. Can be a nested property with keys separated by dots: 'services.signal.server'.
	*/
	getOrThrow(key) {
		const value = getDeep(this._config, key.split("."));
		if (!value) throw new Error(`Config option not present: ${key}`);
		return value;
	}
};
//#endregion
export { parseConfig as a, validateConfig as c, mapToKeyValues as i, getEnvString as n, restoreUnknownFields as o, mapFromKeyValues as r, stripTypeNames as s, Config as t };

//# sourceMappingURL=chunk-config.mjs.map